import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Generate or retrieve session ID for guest users
const getSessionId = () => {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `guest-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(getSessionId());
  const [cartNotification, setCartNotification] = useState(null);

  const API_BASE_URL = '/api/core';

  // Calculate total items and price (ensure cartItems is an array)
  const cartCount = Array.isArray(cartItems) ? cartItems.reduce((total, item) => total + item.quantity, 0) : 0;
  const cartTotal = Array.isArray(cartItems) ? cartItems.reduce((total, item) => total + (parseFloat(item.service.price) * item.quantity), 0) : 0;

  // Fetch cart from API or localStorage
  const fetchCart = async () => {
    try {
      setLoading(true);

      if (isAuthenticated) {
        // Fetch from API for authenticated users
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/cart/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        // Handle both paginated and non-paginated responses
        const items = response.data.results || (Array.isArray(response.data) ? response.data : []);
        setCartItems(items);
      } else {
        // Fetch from API using session ID for guest users
        const response = await axios.get(`${API_BASE_URL}/cart/`, {
          headers: {
            'X-Session-ID': sessionId,
          },
        });

        // Handle both paginated and non-paginated responses
        const items = response.data.results || (Array.isArray(response.data) ? response.data : []);
        setCartItems(items);
      }
    } catch (error) {
      // Only log errors that aren't 404 (cart endpoint may not exist)
      if (error.response?.status !== 404) {
        console.error('Failed to fetch cart:', error.message);
      }
      // Silently set empty cart for 404 errors
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Load cart on mount and when auth status changes
  useEffect(() => {
    fetchCart();
  }, [isAuthenticated]);

  // Add item to cart
  const addToCart = async (serviceId, quantity = 1) => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = isAuthenticated
        ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { 'X-Session-ID': sessionId, 'Content-Type': 'application/json' };

      const response = await axios.post(
        `${API_BASE_URL}/cart/`,
        { service_id: serviceId, quantity },
        { headers }
      );

      // Show notification with service details
      setCartNotification({
        service: response.data.service,
        quantity: response.data.quantity
      });

      // Auto-hide notification after 15 seconds
      setTimeout(() => {
        setCartNotification(null);
      }, 15000);

      // Refresh cart after adding
      await fetchCart();
      return { success: true, data: response.data };
    } catch (error) {
      // Only log non-404 errors
      if (error.response?.status !== 404) {
        console.error('Failed to add to cart:', error.message);
      }

      return {
        success: false,
        error: error.response?.status === 404
          ? 'Cart feature is currently unavailable'
          : (error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to add to cart'),
      };
    }
  };

  // Update cart item quantity
  const updateCartItem = async (cartItemId, quantity) => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = isAuthenticated
        ? { 'Authorization': `Bearer ${token}` }
        : { 'X-Session-ID': sessionId };

      await axios.patch(
        `${API_BASE_URL}/cart/${cartItemId}/`,
        { quantity },
        { headers }
      );

      // Refresh cart after updating
      await fetchCart();
      return { success: true };
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to update cart item:', error.message);
      }
      return {
        success: false,
        error: error.response?.status === 404
          ? 'Cart feature is currently unavailable'
          : (error.response?.data?.error || 'Failed to update cart item'),
      };
    }
  };

  // Remove item from cart
  const removeFromCart = async (cartItemId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = isAuthenticated
        ? { 'Authorization': `Bearer ${token}` }
        : { 'X-Session-ID': sessionId };

      const params = isAuthenticated ? {} : { session_id: sessionId };

      await axios.delete(`${API_BASE_URL}/cart/${cartItemId}/`, {
        headers,
        params,
      });

      // Refresh cart after removing
      await fetchCart();
      return { success: true };
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to remove from cart:', error.message);
      }
      return {
        success: false,
        error: error.response?.status === 404
          ? 'Cart feature is currently unavailable'
          : (error.response?.data?.error || 'Failed to remove from cart'),
      };
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = isAuthenticated
        ? { 'Authorization': `Bearer ${token}` }
        : { 'X-Session-ID': sessionId };

      const data = isAuthenticated ? {} : { session_id: sessionId };

      await axios.post(`${API_BASE_URL}/cart/clear/`, data, { headers });

      setCartItems([]);
      return { success: true };
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to clear cart:', error.message);
      }
      return {
        success: false,
        error: error.response?.status === 404
          ? 'Cart feature is currently unavailable'
          : (error.response?.data?.error || 'Failed to clear cart'),
      };
    }
  };

  // Merge guest cart with user cart on login
  const mergeCart = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/cart/merge/`,
        { session_id: sessionId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      setCartItems(response.data);
      return { success: true };
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to merge cart:', error.message);
      }
      return {
        success: false,
        error: error.response?.status === 404
          ? 'Cart feature is currently unavailable'
          : (error.response?.data?.error || 'Failed to merge cart'),
      };
    }
  };

  const clearCartNotification = () => {
    setCartNotification(null);
  };

  const value = {
    cartItems,
    cartCount,
    cartTotal,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    mergeCart,
    fetchCart,
    cartNotification,
    clearCartNotification,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
